import { useEffect } from 'react';
import { ButtonHeadless, Modal, Spin } from '@wajeez-web/components/ui';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  FormAutocompleteV2,
  FormTextInputV2,
  FormNumberInputV2,
  FormUploadImageInputV2,
} from '@wajeez-web/components/form-builder';
import { Server } from '@wajeez/wajeez.microservices';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { BiLoaderAlt } from 'react-icons/bi';
import {
  PlansActions,
  ProductsActions,
  useProductDispatch,
  useProductSelector,
} from '@wajeez-web/stores/product';
import { enumMapper } from '@wajeez-web/core/utils';

type Input = Server.CreateProductInput & {
  id?: number;
};

interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: () => void | Promise<void>;
  id?: number;
}

const subscriptionPeriodDurations: Record<string, number> = {
  P1D: 86400000,
  P1W: 604800000,
  P1M: 2592000000,
  P3M: 7776000000,
  P6M: 15552000000,
  P1Y: 31536000000,
  P2Y: 63072000000,
  P3Y: 94608000000,
  P5Y: 157680000000,
};

const purchaseTypes = enumMapper(Server.PurchaseType);
const purchaseSources = enumMapper(Server.PurchaseSource);
const productTypes = enumMapper(Server.ProductType);
const purchaseSourceTypes = enumMapper(Server.PurchaseSourceType);

export const ProductModal = ({
  isOpen,
  onClose,
  onSubmit,
  id,
}: ProductModalProps) => {
  const productDispatch = useProductDispatch();

  const createProductStatus = useProductSelector(
    ({ products }) => products.createProduct.status
  );
  const editProductStatus = useProductSelector(
    ({ products }) => products.editProduct.status
  );
  const productById = useProductSelector(
    ({ products }) => products.productById
  );

  const plansLookupsData = useProductSelector(({ plans }) => plans.data);
  const plansLookupsStatus = useProductSelector(({ plans }) => plans.status);

  const { handleSubmit, control, reset } = useForm<Server.CreateProductInput>({
    defaultValues: {},
  });

  const onFormSubmit: SubmitHandler<Input> = async (data) => {
    let res: any;

    const subscriptionPeriod = data.subscriptionPeriod;
    const duration = subscriptionPeriod
      ? subscriptionPeriodDurations[subscriptionPeriod]
      : undefined;

    const mappedData: Input = {
      ...data,
      planId: Number(data.planId),
      purchaseType: Number(data.purchaseType),
      purchaseSource: Number(data.purchaseSource),
      productType: Number(data.productType),
      purchaseSourceType: Number(data.purchaseSourceType),
    };

    if (!id) {
      res = await productDispatch(
        ProductsActions.createProductAsync({
          ...mappedData,
          duration,
        })
      );
    } else {
      res = await productDispatch(
        ProductsActions.editProductAsync({
          id,
          ...mappedData,
        })
      );
    }

    if (res.meta.requestStatus === 'fulfilled') {
      onSubmit && (await onSubmit());
    }
  };

  useEffect(() => {
    if (productById.data) {
      reset(productById.data);
    }
  }, [productById.data]);

  useEffect(() => {
    if (!id) {
      productDispatch(PlansActions.getAllPlansAsync());
    }
  }, [productDispatch, id]);

  useEffect(() => {
    if (id) {
      productDispatch(ProductsActions.getProductByIdAsync(id));
    }

    return () => {
      productDispatch(ProductsActions.clearProductById());
    };
  }, [id, productDispatch]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Product"
      alertTarget="modal"
      fontClassName="font-[Tajawal]"
      withScroll
    >
      <div className="w-full">
        {plansLookupsStatus === 'loading' ||
        productById.status === 'loading' ? (
          <div className="w-full min-h-[200px] flex justify-center items-center">
            <Spin />
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onFormSubmit)}
            noValidate
            className="w-full flex flex-col justify-start items-start"
          >
            <div className="w-[300px] flex flex-col justify-start items-start space-y-2">
              {!id && (
                <div className="w-full">
                  <FormAutocompleteV2
                    name="planId"
                    control={control}
                    fieldMeta={{
                      fieldType: Server.FieldType.Select,
                      fieldOptions: {
                        selectOptions: plansLookupsData.map((p) => {
                          return {
                            value: p.id?.toString() || '',
                            label: p.referenceName || '',
                          };
                        }),
                      },
                      required: true,
                      label: 'Plan',
                    }}
                    required
                  />
                </div>
              )}
              {!id && (
                <div className="w-full">
                  <FormAutocompleteV2
                    name="purchaseType"
                    control={control}
                    fieldMeta={{
                      fieldType: Server.FieldType.Select,
                      fieldOptions: {
                        selectOptions: purchaseTypes,
                      },
                      required: true,
                      label: 'Purchase Type',
                    }}
                    required
                  />
                </div>
              )}
              {!id && (
                <FormTextInputV2
                  name="purchaseProviderKey"
                  control={control}
                  fieldMeta={{
                    fieldType: Server.FieldType.Text,
                    fieldOptions: {},
                    required: true,
                    label: 'Purchase Provider Key',
                    placeHolder: 'Purchase Provider Key',
                  }}
                  required
                />
              )}
              {!id && (
                <div className="w-full">
                  <FormAutocompleteV2
                    name="purchaseSource"
                    control={control}
                    fieldMeta={{
                      fieldType: Server.FieldType.Select,
                      fieldOptions: {
                        selectOptions: purchaseSources,
                      },
                      required: true,
                      label: 'Purchase Source',
                    }}
                    required
                  />
                </div>
              )}
              {!id && (
                <div className="w-full">
                  <FormAutocompleteV2
                    name="productType"
                    control={control}
                    fieldMeta={{
                      fieldType: Server.FieldType.Select,
                      fieldOptions: {
                        selectOptions: productTypes,
                      },
                      required: true,
                      label: 'Product Type',
                    }}
                    required
                  />
                </div>
              )}
              {!id && (
                <FormTextInputV2
                  name="subscriptionPeriod"
                  control={control}
                  fieldMeta={{
                    fieldType: Server.FieldType.Text,
                    fieldOptions: {},
                    required: true,
                    label: 'Subscription Period',
                    placeHolder: 'Subscription Period',
                  }}
                  required
                />
              )}
              {!id && (
                <div className="w-full">
                  <FormAutocompleteV2
                    name="purchaseSourceType"
                    control={control}
                    fieldMeta={{
                      fieldType: Server.FieldType.Select,
                      fieldOptions: {
                        selectOptions: purchaseSourceTypes,
                      },
                      required: true,
                      label: 'Purchase Source Type',
                    }}
                    required
                  />
                </div>
              )}
              <FormTextInputV2
                name="name"
                control={control}
                fieldMeta={{
                  fieldType: Server.FieldType.Text,
                  fieldOptions: {},
                  label: 'Name',
                  placeHolder: 'Name',
                }}
              />
              <FormNumberInputV2
                name="initialAmount"
                control={control}
                fieldMeta={{
                  fieldType: Server.FieldType.Number,
                  fieldOptions: {},
                  label: 'Initial Amount',
                  placeHolder: 'Initial Amount',
                }}
              />
              <FormNumberInputV2
                name="amount"
                control={control}
                fieldMeta={{
                  fieldType: Server.FieldType.Number,
                  fieldOptions: {},
                  label: 'Amount',
                  placeHolder: 'Amount',
                }}
              />
              <FormTextInputV2
                name="currency"
                control={control}
                fieldMeta={{
                  fieldType: Server.FieldType.Text,
                  fieldOptions: {},
                  label: 'Currency',
                  placeHolder: 'Currency',
                }}
              />
              <FormUploadImageInputV2
                name="purchaseSourceIconLightReference"
                control={control}
                fieldMeta={{
                  fieldType: Server.FieldType.UploadImage,
                  fieldOptions: {},
                  label: 'Purchase Source Icon Light Reference',
                }}
              />
              <FormUploadImageInputV2
                name="purchaseSourceIconDarkReference"
                control={control}
                fieldMeta={{
                  fieldType: Server.FieldType.UploadImage,
                  fieldOptions: {},
                  label: 'Purchase Source Icon Dark Reference',
                }}
              />
            </div>
            <div className="mt-6 w-full flex justify-end items-center space-s-2">
              <div className="w-[70px]">
                <ButtonHeadless
                  bgStyle="bg-warmGray-300"
                  borderStyle="border-none"
                  paddingYStyle="py-1"
                  action={onClose}
                >
                  <div className="text-warmGray-600 text-[16px] font-medium">
                    <ReturnTranslatedText
                      text="buttons.Cancel"
                      origintext="Cancel"
                    />
                  </div>
                </ButtonHeadless>
              </div>
              <div className="w-[70px]">
                <ButtonHeadless
                  type="submit"
                  borderStyle="border-none"
                  paddingYStyle="py-1"
                >
                  <div className="text-white text-[16px] font-medium flex justify-center items-center space-s-2">
                    <ReturnTranslatedText
                      text="buttons.Save"
                      origintext="Save"
                    />
                    {(createProductStatus === 'loading' ||
                      editProductStatus === 'loading') && (
                      <BiLoaderAlt className="animate-spin w-5" />
                    )}
                  </div>
                </ButtonHeadless>
              </div>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};
